import * as THREE from 'three';
import type { Apparition } from '../entities/Apparition';
import type { CityMap } from '../game/map';
import { createSeededRandom } from '../utils/random';
import type { LightSource } from '../world/LightSystem';
import type { AudioSystem, DistantSound } from './AudioSystem';

/** What the unease director needs to know each frame. */
export type UneaseFrame = {
  dt: number;
  elapsed: number;
  tension: number;
  her: THREE.Vector3;
  herForward: THREE.Vector3;
  herSpeed: number;
  lookingBack: boolean;
  /** Where he is, if he is out there tonight (and not hidden). */
  him: THREE.Vector3 | null;
  /** He is on screen right now. */
  himVisible: boolean;
  /** 0..1 how much bamboo is right around her. */
  nearBamboo: number;
  camera: THREE.Camera;
  map: CityMap;
  lights: readonly LightSource[];
  lightLevelAt(x: number, z: number): number;
  isVisible(p: THREE.Vector3): boolean;
};

export type UneaseNight = {
  /** A night he doesn't come (the quiet first nights): fewer, softer things. */
  calm: boolean;
  /** Still light out (dusk): no howling dogs or yowling cats. */
  daylight: boolean;
};

/**
 * The little things that make the walk feel wrong, apart from him: one footstep
 * too many when she stops, a whisper close behind, far-off sounds across the
 * neighbourhood, bamboo knocking in the wind, lamps that stutter as he passes
 * under them, and now and then a figure standing far down a dark street that
 * isn't there when she looks again.
 */
export class Unease {
  /** She saw the figure (it was on screen a moment) and it's gone: say something. */
  onFigureGone: (() => void) | null = null;
  private readonly rng = createSeededRandom(1313);
  private night: UneaseNight = { calm: false, daylight: false };
  private prevSpeed = 0;
  private lastEcho = -99;
  private lastWhisper = -99;
  private nextDistant = 30;
  private chimed = false;
  private nextKnock = 0;
  private readonly lampTouched = new Map<LightSource, number>();
  private nextLampTease = 40;
  private nextFigure = 35;
  private figures = 0;
  private figureTime = 0;
  private figureSeen = 0;
  private figureUnseen = 0;
  private readonly tmp = new THREE.Vector3();
  private readonly right = new THREE.Vector3();

  constructor(
    private readonly audio: AudioSystem,
    private readonly figure: Apparition,
  ) {}

  setNight(night: UneaseNight): void {
    this.night = night;
    this.reset();
  }

  reset(): void {
    this.prevSpeed = 0;
    this.lastEcho = -99;
    this.lastWhisper = -99;
    this.nextDistant = 25 + this.rng() * 25;
    this.chimed = false;
    this.nextKnock = 0;
    this.lampTouched.clear();
    this.nextLampTease = 40 + this.rng() * 40;
    this.nextFigure = 30 + this.rng() * 40;
    this.figures = 0;
    this.figure.hide();
  }

  /** Dev: stand the figure on the street ahead of her right now (false if there's no open street). */
  forceFigure(her: THREE.Vector3, forward: THREE.Vector3, map: CityMap): boolean {
    for (const dist of [24, 20, 28, 16, 32, 12]) {
      const x = her.x + forward.x * dist;
      const z = her.z + forward.z * dist;
      if (!map.isWalkable(x, z, 0.5) || !map.lineOfSight(her.x, her.z, x, z)) continue;
      this.figure.show(x, z, her);
      this.figureTime = 0;
      this.figureSeen = 0;
      this.figureUnseen = 0;
      return true;
    }
    return false;
  }

  /** Dev: let the figure appear as soon as there is a spot for it. */
  hurryFigure(): void {
    this.nextFigure = 0;
    this.figures = 0;
  }

  update(f: UneaseFrame): void {
    this.right.set(1, 0, 0).applyQuaternion(f.camera.quaternion);
    this.echoes(f);
    this.whispers(f);
    this.distantSounds(f);
    this.bamboo(f);
    this.lamps(f);
    this.figureAhead(f);
    this.prevSpeed = f.herSpeed;
  }

  /** Stereo pan (-1..1) of a world point from where the camera looks. */
  private pan(f: UneaseFrame, x: number, z: number): number {
    const dx = x - f.her.x;
    const dz = z - f.her.z;
    const d = Math.hypot(dx, dz);
    return d > 0.01 ? ((dx * this.right.x + dz * this.right.z) / d) * 0.8 : 0;
  }

  /**
   * She stops... and a beat later, one more step behind her. If he's there,
   * it's his. If he isn't, it's nobody's.
   */
  private echoes(f: UneaseFrame): void {
    const stopped = this.prevSpeed > 0.8 && f.herSpeed < 0.15;
    if (!stopped || f.lookingBack || f.elapsed - this.lastEcho < ECHO_GAP) return;
    if (f.him && !f.himVisible) {
      const d = Math.hypot(f.him.x - f.her.x, f.him.z - f.her.z);
      if (d < 5 || d > 34) return;
      this.lastEcho = f.elapsed;
      const vol = Math.pow(1 - d / 40, 1.3);
      this.audio.phantomStep(vol, this.pan(f, f.him.x, f.him.z), 0.32 + this.rng() * 0.2);
    } else if (!f.him && !this.night.calm && f.tension > 0.2 && this.rng() < 0.35) {
      this.lastEcho = f.elapsed;
      this.audio.phantomStep(0.3 + this.rng() * 0.2, (this.rng() - 0.5) * 0.7, 0.4 + this.rng() * 0.25);
    }
  }

  /** Him close behind her, unseen, when she is already frightened: breath at her ear. */
  private whispers(f: UneaseFrame): void {
    if (!f.him || f.himVisible || f.tension < 0.55 || f.elapsed - this.lastWhisper < WHISPER_GAP) return;
    const dx = f.him.x - f.her.x;
    const dz = f.him.z - f.her.z;
    const d = Math.hypot(dx, dz);
    const behind = dx * f.herForward.x + dz * f.herForward.z < 0;
    if (d > 6 || !behind) return;
    this.lastWhisper = f.elapsed;
    this.audio.whisper(this.pan(f, f.him.x, f.him.z), 0.7 + (1 - d / 6) * 0.3);
  }

  /** Every minute or so, something somewhere across the neighbourhood. */
  private distantSounds(f: UneaseFrame): void {
    if (f.elapsed < this.nextDistant) return;
    this.nextDistant = f.elapsed + (this.night.calm ? 60 : 38) + this.rng() * 50;
    const pool: DistantSound[] = this.night.daylight ? ['slam', 'clang'] : ['howl', 'clang', 'slam', 'cry', 'howl'];
    // The warped ward chime: once a night, a while in.
    if (!this.chimed && f.elapsed > 50 && this.rng() < 0.5) {
      this.chimed = true;
      this.audio.distant('chime', 0.8, (this.rng() - 0.5) * 1.4);
      return;
    }
    const kind = pool[Math.floor(this.rng() * pool.length)];
    this.audio.distant(kind, 0.55 + this.rng() * 0.45, (this.rng() - 0.5) * 1.8);
  }

  /** Bamboo around her knocks together when a gust comes through. */
  private bamboo(f: UneaseFrame): void {
    if (f.nearBamboo <= 0 || this.audio.gust < 0.3 || f.elapsed < this.nextKnock) return;
    this.nextKnock = f.elapsed + 0.7 + this.rng() * 2;
    this.audio.bambooKnock(f.nearBamboo * Math.min(1, this.audio.gust), (this.rng() - 0.5) * 1.6);
  }

  /**
   * Lamps stutter as he walks under them (so a flicker behind her means
   * something). On the nights he doesn't come, now and then one just does it
   * as she gets near.
   */
  private lamps(f: UneaseFrame): void {
    if (f.him) {
      for (const l of f.lights) {
        if (l.state !== 'on') continue;
        const d = Math.hypot(l.ground.x - f.him.x, l.ground.y - f.him.z);
        if (d > LAMP_NEAR_HIM) continue;
        const last = this.lampTouched.get(l) ?? -99;
        if (f.elapsed - last < 25) continue;
        this.lampTouched.set(l, f.elapsed);
        if (this.rng() > 0.55) continue;
        l.disturb(0.7 + this.rng() * 0.6);
        const dh = Math.hypot(l.ground.x - f.her.x, l.ground.y - f.her.z);
        this.audio.lampZap(Math.max(0, 1 - dh / 30), this.pan(f, l.ground.x, l.ground.y));
      }
      return;
    }
    if (f.elapsed < this.nextLampTease) return;
    this.nextLampTease = f.elapsed + 45 + this.rng() * 50;
    // The lamp just ahead of her, if there is one.
    let best: LightSource | null = null;
    let bestD = 14;
    for (const l of f.lights) {
      if (l.state !== 'on') continue;
      const dx = l.ground.x - f.her.x;
      const dz = l.ground.y - f.her.z;
      const d = Math.hypot(dx, dz);
      if (d < bestD && d > 4 && dx * f.herForward.x + dz * f.herForward.z > 0) {
        best = l;
        bestD = d;
      }
    }
    if (!best) return;
    best.disturb(1.1);
    this.audio.lampZap(Math.max(0, 1 - bestD / 30), this.pan(f, best.ground.x, best.ground.y));
  }

  /** The figure far down the street. */
  private figureAhead(f: UneaseFrame): void {
    const fig = this.figure;
    if (fig.visible) {
      this.figureTime += f.dt;
      const onScreen = f.isVisible(this.tmp.copy(fig.position).setY(1.4));
      if (onScreen) {
        this.figureSeen += f.dt;
        this.figureUnseen = 0;
      } else {
        this.figureUnseen += f.dt;
      }
      const d = Math.hypot(fig.position.x - f.her.x, fig.position.z - f.her.z);
      // Gone when she gets close, when she looks away after seeing it, or after a while anyway.
      const gone = d < FIGURE_VANISH || (this.figureSeen > 0.8 && this.figureUnseen > 0.25) || this.figureSeen > 3.2 || this.figureTime > 14;
      if (gone) {
        fig.hide();
        // A lamp near where it stood gives a twitch as it goes.
        for (const l of f.lights) {
          if (l.state === 'on' && Math.hypot(l.ground.x - fig.position.x, l.ground.y - fig.position.z) < 12) l.disturb(0.6);
        }
        if (this.figureSeen > 0.8) this.onFigureGone?.();
      }
      return;
    }
    if (f.elapsed < this.nextFigure || this.figures >= (this.night.calm ? 1 : 2)) return;
    this.nextFigure = f.elapsed + 3;
    if (f.lookingBack || f.herSpeed < 0.3) return;
    // A dark, walkable spot well ahead along her line of sight, away from him.
    for (const dist of [30, 27, 34, 24, 38, 21, 42]) {
      for (const side of [1.4, -1.4, 0, 2.6, -2.6]) {
        const x = f.her.x + f.herForward.x * dist - f.herForward.z * side;
        const z = f.her.z + f.herForward.z * dist + f.herForward.x * side;
        if (!f.map.isWalkable(x, z, 0.6) || !f.map.lineOfSight(f.her.x, f.her.z, x, z)) continue;
        // Backlit: in the dim just short of a lamp's pool, so it stands black against the glow.
        if (f.lightLevelAt(x, z) > FIGURE_DIM) continue;
        const bx = x + f.herForward.x * 5;
        const bz = z + f.herForward.z * 5;
        if (Math.max(f.lightLevelAt(bx, bz), f.lightLevelAt(bx + f.herForward.x * 3, bz + f.herForward.z * 3)) < FIGURE_GLOW) continue;
        if (f.him && Math.hypot(f.him.x - x, f.him.z - z) < 15) continue;
        fig.show(x, z, f.her);
        this.figures += 1;
        this.figureTime = 0;
        this.figureSeen = 0;
        this.figureUnseen = 0;
        this.nextFigure = f.elapsed + 70 + this.rng() * 60;
        return;
      }
    }
  }
}

/** Seconds between extra footsteps, and between whispers. */
const ECHO_GAP = 18;
const WHISPER_GAP = 35;
/** He makes a lamp stutter when he passes within this distance of it (m). */
const LAMP_NEAR_HIM = 4.5;
/** The figure is gone once she is this close (m). */
const FIGURE_VANISH = 15;
/** The figure stands where the light is at most FIGURE_DIM, with at least FIGURE_GLOW a few metres behind it. */
const FIGURE_DIM = 0.55;
const FIGURE_GLOW = 0.6;
